/**
 * As regras dos anexos do chamado (2026-10-20, decisão da dona: imagem,
 * PDF e planilha, até 3 arquivos de 5 MB, e print colado com Ctrl+V).
 *
 * Os limites vêm da API (`GET /me/support`), nunca de constante daqui: o
 * dono é `web_api/application/support.py`. A conferência desta tela é
 * pela EXTENSÃO, só pra avisar antes de mandar; quem decide é o servidor,
 * que confere pelos bytes.
 */

export type AttachmentLimits = {
  max: number;
  maxBytes: number;
  /** Com o ponto e em minúsculas: `.png`, `.xlsx`… */
  extensions: string[];
};

export function acceptsName(name: string, extensions: string[]): boolean {
  const lower = name.toLowerCase();
  return extensions.some(extension => lower.endsWith(extension));
}

/** "340 KB", "1,2 MB" — o que a pessoa compara com o limite. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".0", "").replace(".", ",")} MB`;
}

/** Junta os novos aos que já estão, na ordem: o que passa entra, o que
 *  não passa vira UMA mensagem (a do primeiro problema), e o mesmo arquivo
 *  duas vezes (nome e tamanho iguais) entra uma vez só. */
export function addAttachments(
  current: File[],
  incoming: File[],
  limits: AttachmentLimits
): { files: File[]; error: string | null } {
  const files = [...current];
  let error: string | null = null;
  const fail = (message: string) => {
    error ??= message;
  };
  for (const file of incoming) {
    if (files.some(item => item.name === file.name && item.size === file.size)) continue;
    if (!acceptsName(file.name, limits.extensions)) {
      fail(`"${file.name}" não é um tipo aceito. Envie imagem (PNG, JPG ou WebP), PDF ou planilha (XLSX ou CSV).`);
      continue;
    }
    if (file.size === 0) {
      fail(`"${file.name}" está vazio.`);
      continue;
    }
    if (file.size > limits.maxBytes) {
      fail(`"${file.name}" passa de ${formatBytes(limits.maxBytes)}.`);
      continue;
    }
    if (files.length >= limits.max) {
      fail(`Envie no máximo ${limits.max} arquivos por chamado.`);
      continue;
    }
    files.push(file);
  }
  return { files, error };
}

const pad = (value: number) => String(value).padStart(2, "0");

/** O print colado chega como `image.png`; três deles teriam o mesmo nome
 *  no chamado. Ganha a hora em que foi colado. */
export function pastedFiles(files: File[], now: Date): File[] {
  return files.map((file, index) => {
    if (!file.type.startsWith("image/")) return file;
    const extension = file.type === "image/jpeg" ? "jpg" : file.type.slice("image/".length);
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}h${pad(now.getMinutes())}m${pad(now.getSeconds())}`;
    const suffix = files.length > 1 ? `-${index + 1}` : "";
    return new File([file], `print-${stamp}${suffix}.${extension}`, { type: file.type });
  });
}
